import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const PrivacyPolicyPage = () => (
  <div className="legal-page">
    <div className="legal-container">
      <Link to="/" className="legal-back-link">← Back to Home</Link>

      <div className="legal-header">
        <span className="legal-eyebrow">Legal</span>
        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-updated">Last updated: 18 March 2026</p>
      </div>

      <div className="legal-body">
        <p>
          <strong>Batos IT</strong> ("we", "us", "our") operates the CodeHub platform at{' '}
          <a href="https://codeninja.batosit.com">codeninja.batosit.com</a>. This Privacy Policy explains how we collect,
          use, store, and protect your personal information when you use our platform.
        </p>

        <h2>1. Introduction</h2>
        <p>
          This Privacy Policy describes how CodeHub collects, uses, processes, stores, and protects personal information
          when users visit the website, create an account, enroll in internships, make payments, submit projects, or
          contact support. By using the platform, you consent to the practices described in this policy.
        </p>
        <p>
          This policy applies to all services provided through CodeHub by <strong>Batos IT</strong>, including
          registration, internship enrollment, payment collection, support communication, review workflows, and certificate
          issuance.
        </p>
        <p>
          CodeHub is a digital platform that provides online internship enrollment, project workflow management, mentor
          support, review processing, and certificate issuance. By visiting or using the website, you agree to the data
          handling practices described in this Privacy Policy. If you do not agree with this policy, you should not use
          the platform.
        </p>

        <h2>2. Information We Collect</h2>
        <h3>a) Information You Provide</h3>
        <ul>
          <li><strong>Account Information:</strong> Full name, email address, phone number, college name, and course name during registration.</li>
          <li><strong>Payment Information:</strong> We receive limited payment-related information such as transaction ID, payment status, payment method, order reference, and amount paid.</li>
          <li><strong>Project Submissions:</strong> Code, GitHub repository links, and other project-related content you submit.</li>
          <li><strong>Support Requests:</strong> Messages and communications sent through the platform's support feature.</li>
          <li><strong>Certificate Information:</strong> Your name, project title, certificate ID, and completion-related records when a certificate is issued.</li>
        </ul>

        <h3>b) Information Collected Automatically</h3>
        <ul>
          <li><strong>Usage Data:</strong> Pages visited, features used, and interactions with the platform.</li>
          <li><strong>Device Information:</strong> Browser type, operating system, and IP address.</li>
          <li><strong>UTM Parameters:</strong> Referral source, campaign, and medium data from the URL used to access the platform.</li>
          <li><strong>Technical Logs:</strong> Authentication events, error logs, and access metadata used for security, monitoring, and troubleshooting.</li>
        </ul>

        <h2>3. How We Use Data</h2>
        <ul>
          <li>To create and manage your account</li>
          <li>To process internship enrollment and payments</li>
          <li>To provide mentor support and review project submissions</li>
          <li>To issue internship completion certificates</li>
          <li>To send transactional emails (verification, payment confirmation, review updates, certificate)</li>
          <li>To improve the platform and user experience</li>
          <li>To prevent fraud and ensure platform security</li>
          <li>To maintain internal business records, customer support history, and payment reconciliation</li>
        </ul>
        <p>
          We use your information only for legitimate business and operational purposes related to providing the CodeHub
          platform and fulfilling services you request through the website.
        </p>

        <div className="legal-info">
          <strong>📧 Email Communications:</strong> We send transactional emails only (account verification, payment receipts, review notifications, certificates).
          We do not send marketing or promotional emails without your explicit consent.
        </div>

        <h2>4. Payment Processing</h2>
        <p>
          Payments on CodeHub are processed securely through <strong>Razorpay</strong>, our third-party payment gateway provider.
          When you make a payment, you may be redirected to or interact with Razorpay's secure payment systems to complete
          the transaction.
        </p>
        <ul>
          <li>We do <strong>not</strong> store full card numbers, CVV, UPI PINs, net banking credentials, or bank account login information on our servers.</li>
          <li>We may store payment references such as order IDs, transaction IDs, payment status, amount, and timestamps for reconciliation and support.</li>
          <li>Payment information shared directly with Razorpay is governed by Razorpay's own privacy and security practices.</li>
          <li>If a bank or payment partner declines, reverses, or flags a transaction, we may retain the related payment status and failure metadata for audit, support, and dispute handling.</li>
        </ul>

        <h2>5. Data Sharing</h2>
        <p>We do <strong>not sell, rent, or share</strong> your personal information with third parties, except:</p>
        <ul>
          <li><strong>Payment Processor:</strong> Razorpay processes your payments. Their privacy policy applies to payment data.</li>
          <li><strong>Service Providers:</strong> Hosting, email, and infrastructure partners who help us operate the platform under confidentiality and security obligations.</li>
          <li><strong>Legal Compliance:</strong> If required by law, regulation, or legal process.</li>
          <li><strong>Certificate Verification:</strong> Your name, project title, and certificate ID are accessible via the public certificate verification page.</li>
        </ul>
        <p>
          We do not sell your personal information to advertisers, data brokers, or unrelated third parties.
        </p>

        <h2>6. Cookies</h2>
        <p>
          We use essential cookies and local storage to maintain your login session and preferences.
          We do <strong>not</strong> use third-party tracking cookies or advertising cookies.
        </p>
        <p>
          Cookies and similar browser storage may also be used for authentication, security checks, remembering basic
          user preferences, and improving session continuity while you use the platform.
        </p>

        <h2>7. Data Security</h2>
        <ul>
          <li>Your data is stored on secure servers hosted on Amazon Web Services (AWS) in the Mumbai region.</li>
          <li>All data transmission is protected using HTTPS/TLS encryption.</li>
          <li>Passwords are hashed using industry-standard algorithms and are never stored in plaintext.</li>
          <li>Access to personal information is restricted to authorized personnel who require it to operate and support the platform.</li>
          <li>While we take reasonable technical and organizational measures to protect your data, no internet-based system can be guaranteed to be completely secure.</li>
        </ul>
        <p>
          We retain personal data for as long as necessary to operate your account, provide services, comply with applicable
          legal, tax, and accounting obligations, resolve disputes, and maintain certificate verification records.
        </p>
        <p>
          CodeHub is not intended for children under 16 years of age. If we become aware that personal data of a child
          under 16 has been collected without appropriate authorization, we will take reasonable steps to delete it.
        </p>

        <h2>8. User Rights</h2>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access</strong> your personal data stored on the platform</li>
          <li><strong>Correct</strong> inaccurate information in your profile</li>
          <li><strong>Delete</strong> your account by contacting our support team</li>
          <li><strong>Withdraw Consent</strong> for optional data processing</li>
          <li><strong>Request Clarification</strong> regarding how your data is used in connection with CodeHub services</li>
        </ul>
        <p>
          To exercise any of these rights, contact us at <a href="mailto:connect@batosit.com">connect@batosit.com</a>.
        </p>
        <p>
          Certain records may still be retained where required for legal compliance, fraud prevention, transaction history,
          certificate verification, or dispute resolution.
        </p>

        <h2>9. Contact Information</h2>
        <p>For any privacy-related questions, requests, or concerns:</p>

        <div className="legal-contact">
          <h3>Batos IT</h3>
          <p>
            Sterling Towers, Level 1, 2 & 3, Pan Card Club Road, Baner, Pune 411045<br />
            Email: <a href="mailto:connect@batosit.com">connect@batosit.com</a><br />
            Website: <a href="https://codeninja.batosit.com">www.codeninja.batosit.com</a>
          </p>
        </div>
        <p>
          You can also review our related legal pages here: <Link to="/terms">Terms & Conditions</Link>,{' '}
          <Link to="/refund-policy">Refund Policy</Link>, and <Link to="/cancellation-policy">Cancellation Policy</Link>.
        </p>

        <h2>10. Last Updated Date</h2>
        <p>
          This Privacy Policy was last updated on <strong>18 March 2026</strong>. We may revise this policy from time to time,
          and any updates will be published on this page with the revised date.
        </p>
      </div>
    </div>
  </div>
);

export default PrivacyPolicyPage;
